import { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../api/services/apiService";
import type { API } from "../types";
import {
  buildPortfolioApiRow,
  computePortfolioSummary,
  sortPortfolioRows,
  type PortfolioApiRow,
  type PortfolioSummary,
} from "../utils/portfolioIntelligenceUtils";

export function usePortfolioIntelligence() {
  const [apis, setApis] = useState<API[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scannedAt, setScannedAt] = useState<Date>(new Date());

  const fetchPortfolio = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.getApis();
      setApis(list);
      setScannedAt(new Date());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const rows = useMemo(() => apis.map(buildPortfolioApiRow), [apis]);

  const summary: PortfolioSummary = useMemo(
    () => computePortfolioSummary(apis, scannedAt),
    [apis, scannedAt],
  );

  const sortedRows: PortfolioApiRow[] = useMemo(
    () => sortPortfolioRows(rows, "duplicateScore", "desc"),
    [rows],
  );

  return {
    apis,
    rows: sortedRows,
    summary,
    loading,
    error,
    refresh: fetchPortfolio,
  };
}
